'use client'

import dynamic from 'next/dynamic'
import Image, { type ImageProps } from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { buildFileUrl, getFileAsset } from '@sanity/asset-utils'
import { stegaClean } from 'next-sanity'

import { decompressLottie } from '@redbrickmedia/brickster-shared/lottie'

import { cn } from '@/app/utils/style'
import type { SideBySideSection } from '@/sanity.types'
import { urlForImage } from '@/sanity/lib/utils'

// Extract image type with Lottie support from generated Sanity types
export type SanityImageWithLottie = NonNullable<SideBySideSection['items'][number]['image']>

const LottieReact = dynamic(() => import('lottie-react').then((m) => m.default), {
  ssr: true,
  loading: () => <div className="w-full h-full" />,
})

export type MediaProps = Omit<ImageProps, 'src' | 'alt' | 'loader' | 'fill'> & {
  image?: SanityImageWithLottie
  classNames?: {
    root?: string
    lottie?: string
    image?: string
  }
  /**
   * When true, disables Sanity's automatic image cropping by overriding the crop rectangle
   * to use the full native image dimensions. Useful when you want to display the complete
   * uncropped image while still maintaining aspect ratio and Lottie support.
   *
   * Technical: Uses `.rect(0, 0, width, height)` to override any crop/hotspot data,
   * and requests the native image dimensions instead of scaled versions.
   */
  ignoreCrop?: boolean
  /**
   * When true, disables viewport-based play/pause control for Lottie animations.
   * Use with `shouldPlay` for external playback control (e.g., in sliders).
   */
  disableViewportControl?: boolean
  /**
   * External control for Lottie playback. When provided with disableViewportControl:
   * - true: plays animation from first frame
   * - false: pauses animation
   */
  shouldPlay?: boolean
  /**
   * Callback fired when Lottie animation starts playing.
   * Useful for synchronizing external UI (e.g., progress bars) with animation start.
   */
  onLottiePlay?: () => void
  /**
   * When true, applies cover-like behavior with 1.3x zoom.
   * Uses CSS transform instead of canvas manipulation.
   */
  coverMode?: boolean
  /**
   * When false, skips applying aspect-ratio style to Lottie animation container.
   * Defaults to true. Set to false for mobile fallback scenarios where natural sizing is needed.
   * Only affects Lottie animations - regular images are not affected.
   */
  applyLottieAspectRatio?: boolean
  /**
   * When defined, enables lazy loading for Lottie files with the specified margin in pixels.
   * Lottie will load when component is within this distance from viewport.
   * Example: lazyLoadMargin={200} loads when within 200px of viewport.
   * If undefined, Lottie loads immediately. Ignored if priority={true}.
   */
  lazyLoadMargin?: number
}

export function Media({
  image,
  classNames,
  ignoreCrop = false,
  disableViewportControl = false,
  shouldPlay,
  onLottiePlay,
  coverMode = false,
  applyLottieAspectRatio = true,
  lazyLoadMargin,
  priority,
  ...imgProps
}: MediaProps) {
  // Lazy load Lottie files when near viewport (only if lazyLoadMargin is defined and priority is not set)
  const shouldEnableLazyLoad = lazyLoadMargin !== undefined && priority !== true

  const [lottieInViewRef, lottieInView] = useInView({
    rootMargin: `${lazyLoadMargin ?? 0}px`,
    threshold: 0,
    triggerOnce: true,
    skip: !shouldEnableLazyLoad,
  })

  // Refs for Lottie viewport control (must be called before any returns)
  const containerRef = useRef<HTMLDivElement>(null)
  const dotLottieRef = useRef<any>(null)
  const [isDotLottieReady, setIsDotLottieReady] = useState(false)
  const [svgAnimationData, setSvgAnimationData] = useState<any>(null)

  const projectId = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID
  const dataset = process.env.NEXT_PUBLIC_SANITY_DATASET

  const lottieRef = image?.lottieFile?.asset?._ref
  const isLottie = image?.mediaType === 'lottie' && !!lottieRef

  // Fetch and decompress animation data for SVG renderer (.lottie files are ZIP archives)
  useEffect(() => {
    if (!isLottie || !projectId || !dataset) return

    const fetchAnimationData = async () => {
      try {
        const fileAsset = getFileAsset(lottieRef!, { projectId, dataset })
        const fileUrl = buildFileUrl(fileAsset, { projectId, dataset })

        // Use brickster-shared decompression utility
        const animationData = await decompressLottie(fileUrl)

        if (animationData) {
          setSvgAnimationData(animationData)
        }
      } catch (error) {
        console.error('[Media] Failed to load animation data for SVG renderer:', error)
      }
    }

    fetchAnimationData()
  }, [isLottie, lottieRef, projectId, dataset])

  // Track when SVG renderer instance is ready
  useEffect(() => {
    if (dotLottieRef.current) {
      setIsDotLottieReady(true)
    }
  }, [svgAnimationData])

  // Pause/play Lottie animations based on viewport visibility (when viewport control is enabled)
  useEffect(() => {
    if (!isLottie || !containerRef.current || !dotLottieRef.current || disableViewportControl)
      return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (dotLottieRef.current) {
            if (entry.isIntersecting) {
              dotLottieRef.current.play()
            } else {
              dotLottieRef.current.pause()
            }
          }
        })
      },
      { threshold: 0.1 },
    )

    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [isLottie, disableViewportControl])

  // External playback control (when viewport control is disabled)
  useEffect(() => {
    if (!isLottie || !disableViewportControl) return
    if (!dotLottieRef.current || !isDotLottieReady) return

    if (shouldPlay) {
      dotLottieRef.current.goToAndStop(0, true)
      dotLottieRef.current.play()
      // Call callback immediately when play starts
      if (onLottiePlay) {
        onLottiePlay()
      }
    } else {
      dotLottieRef.current.pause()
    }
  }, [isLottie, disableViewportControl, shouldPlay, isDotLottieReady, onLottiePlay])

  if (!image) return null

  if (isLottie) {
    if (!projectId || !dataset) return null

    const aspectRatioStyle =
      applyLottieAspectRatio && image.lottieWidth && image.lottieHeight
        ? { aspectRatio: `${image.lottieWidth} / ${image.lottieHeight}` }
        : undefined

    // Combine refs for both lazy loading detection and viewport control
    const setRefs = (node: HTMLDivElement | null) => {
      lottieInViewRef(node)
      containerRef.current = node
    }

    // Only load Lottie when in view (or if lazy loading is disabled)
    const shouldLoadLottie = !shouldEnableLazyLoad || lottieInView

    return (
      <div
        ref={setRefs}
        className={cn('relative w-full', classNames?.root, coverMode && 'overflow-hidden')}
        style={aspectRatioStyle}
        title={stegaClean(image.alt) || undefined}
      >
        {shouldLoadLottie ? (
          svgAnimationData ? (
            <LottieReact
              animationData={svgAnimationData}
              loop={image.loop ?? true}
              autoplay={disableViewportControl ? shouldPlay : (image.autoplay ?? true)}
              className={cn(classNames?.lottie, coverMode && 'scale-130 object-cover')}
              style={
                coverMode
                  ? {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }
                  : undefined
              }
              lottieRef={dotLottieRef}
            />
          ) : (
            <div className={cn('w-full h-full', classNames?.lottie)} />
          )
        ) : (
          <div className={cn('w-full h-full', classNames?.lottie)} />
        )}
      </div>
    )
  }

  if (!image.asset) return null

  /**
   * Extract native image dimensions from Sanity asset reference.
   * The _ref format is: image-{assetId}-{width}x{height}-{format}
   * Example: "image-abc123def456-2288x976-png"
   *
   * We prioritize parsing from _ref for accuracy since it always contains
   * the actual uploaded image dimensions, whereas metadata may not be available
   * in all query projections.
   */
  const asset = image.asset as any
  const ref = asset?._ref || ''
  const match = ref.match(/-(\d+)x(\d+)-/)

  let imageWidth: number
  let imageHeight: number

  if (match) {
    imageWidth = parseInt(match[1], 10)
    imageHeight = parseInt(match[2], 10)
  } else {
    // Fallback to metadata or safe defaults if regex fails
    imageWidth = asset?.metadata?.dimensions?.width || 800
    imageHeight = asset?.metadata?.dimensions?.height || 600
  }

  /**
   * Build Sanity CDN URL with or without cropping.
   *
   * Normal mode: Scales image to requested width while maintaining aspect ratio.
   * ignoreCrop mode: Overrides any crop/hotspot with full image rect and requests
   * native dimensions. This bypasses Sanity's automatic cropping while preserving
   * the imageUrlBuilder pipeline (auto format, quality optimization, etc).
   */
  const buildUrl = (width?: number) => {
    let builder = urlForImage(image)
    if (!builder) return ''

    if (ignoreCrop) {
      // Override crop rectangle to full image dimensions and request native size
      builder = builder.rect(0, 0, imageWidth, imageHeight).width(imageWidth).height(imageHeight)
    } else if (width) {
      // Scale to requested width while maintaining aspect ratio
      builder = builder.width(width).height(Math.round((width * imageHeight) / imageWidth))
    }

    return builder.url()
  }

  const baseUrl = buildUrl()
  if (!baseUrl) return null

  return (
    <div className={cn('w-full', classNames?.root)}>
      <Image
        loader={({ width }) => buildUrl(width) || baseUrl}
        src={baseUrl}
        alt={stegaClean(image.alt) || ''}
        width={imageWidth}
        height={imageHeight}
        className={cn('w-full h-auto', classNames?.image)}
        {...imgProps}
      />
    </div>
  )
}
